import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, CheckCircle, X } from 'lucide-react';

// ── Generic, config-driven Dashboard filter modal ────────────────────────────
// The Dashboard hosts many dashboards (Deployment sub-views, Attendance, Red
// Flag, Mood, Appeals), each needing a DIFFERENT set of filters. Rather than a
// per-dashboard sidebar, the page passes a `sections` array describing exactly
// which controls to render for the ACTIVE dashboard; this modal renders them.
// Every dashboard always includes the default "Roles" + "Month" sections (the
// page composes those in). All controls update LIVE (no Apply) — the same
// instant-filter UX the old Deployment drawer had.

export interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

export type FilterSection =
  | {
      kind: 'text';
      key: string;
      label: string;
      value: string;
      onChange: (v: string) => void;
      placeholder?: string;
    }
  | {
      kind: 'multiselect';
      key: string;
      label: string;
      options: FilterOption[];
      selected: string[];
      onToggle: (v: string) => void;
      onClear?: () => void;
      searchable?: boolean;
      /** What the closed field says when nothing is ticked; "All <label>" when left out. */
      allLabel?: string;
    }
  | {
      kind: 'month';
      key: string;
      label: string;
      value: string;
      options: FilterOption[];
      onChange: (v: string) => void;
    }
  | {
      // Single-select dropdown (native <select>, overlays like the month picker).
      // For one-of-many filters (e.g. a report's Mood / Category / City / State).
      kind: 'select';
      key: string;
      label: string;
      value: string;
      options: FilterOption[];
      onChange: (v: string) => void;
    }
  | {
      kind: 'daterange';
      key: string;
      label: string;
      from: string;
      to: string;
      onFromChange: (v: string) => void;
      onToChange: (v: string) => void;
    };

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  sections: FilterSection[];
  /** Number of active (non-empty) filters — shown in the header + drives "Clear all". */
  activeCount: number;
  onClearAll: () => void;
}

// One multi-select control. Collapsed to a summary button; expands INLINE (not a
// popover) so it can never be clipped by the modal's own scroll container.
const MultiSelectField = ({
  label,
  options,
  selected,
  onToggle,
  onClear,
  searchable,
  allLabel,
}: {
  label: string;
  options: FilterOption[];
  selected: string[];
  onToggle: (v: string) => void;
  onClear?: () => void;
  searchable?: boolean;
  allLabel?: string;
}) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  // Fixed-position box anchored to the button — the list OVERLAYS the modal (like
  // a native <select>) instead of expanding inline, which grew the row and left
  // blank space in the sibling column of the 2-col grid.
  const [pos, setPos] = useState<{ left: number; width: number; top?: number; bottom?: number } | null>(null);

  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? options.filter((o) => o.label.toLowerCase().includes(t)) : options;
  }, [options, q]);
  const summary = selected.length === 0 ? (allLabel ?? `All ${label}`) : `${selected.length} selected`;

  // Position the popover; reposition on scroll (incl. the modal body's own scroll,
  // hence capture=true) + resize. Flip above the button when there's no room below.
  useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (!r) return;
      const H = 300; // approx max popover height
      const below = window.innerHeight - r.bottom;
      const openUp = below < H && r.top > below;
      setPos({
        left: r.left,
        width: r.width,
        ...(openUp ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }),
      });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || popRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div>
      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
        {label}
      </label>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm hover:border-gray-300 focus:border-primary-500 focus:outline-none"
      >
        <span className={`truncate ${selected.length > 0 ? 'font-semibold text-gray-800' : 'text-gray-500'}`}>
          {summary}
        </span>
        <ChevronDown className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && pos && createPortal(
        <div
          ref={popRef}
          style={{ position: 'fixed', left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, zIndex: 70 }}
          className="rounded-lg border border-gray-200 bg-white shadow-lg"
        >
          {searchable && options.length > 6 && (
            <div className="border-b border-gray-100 p-2">
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={`Search ${label}…`}
                autoFocus
                className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          )}
          <div className="max-h-52 overflow-y-auto py-1">
            {shown.map((o) => {
              const on = selected.includes(o.value);
              return (
                <div
                  key={o.value}
                  onClick={() => onToggle(o.value)}
                  className={`flex cursor-pointer items-center justify-between px-3 py-1.5 text-sm hover:bg-gray-50 ${on ? 'bg-primary-50 text-primary-700' : 'text-gray-700'}`}
                >
                  <span className="truncate">
                    {o.label}
                    {o.count != null ? <span className="ml-1 text-xs text-gray-400">({o.count})</span> : null}
                  </span>
                  {on && <CheckCircle className="h-4 w-4 flex-shrink-0 text-primary-600" />}
                </div>
              );
            })}
            {shown.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-400">No options</div>
            )}
          </div>
          {selected.length > 0 && onClear && (
            <div className="border-t border-gray-100 p-1.5">
              <button
                type="button"
                onClick={onClear}
                className="w-full rounded px-2 py-1 text-xs font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-700"
              >
                Clear {label}
              </button>
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
};

const renderSection = (s: FilterSection) => {
  switch (s.kind) {
    case 'text':
      return (
        <div key={s.key}>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
            {s.label}
          </label>
          <input
            type="text"
            value={s.value}
            onChange={(e) => s.onChange(e.target.value)}
            placeholder={s.placeholder ?? `Search ${s.label}…`}
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none"
          />
        </div>
      );
    case 'month':
      return (
        <div key={s.key}>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
            {s.label}
          </label>
          <select
            value={s.value}
            onChange={(e) => s.onChange(e.target.value)}
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm focus:border-primary-500 focus:outline-none"
          >
            {s.options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      );
    case 'select':
      return (
        <div key={s.key}>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
            {s.label}
          </label>
          <select
            value={s.value}
            onChange={(e) => s.onChange(e.target.value)}
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-primary-500 focus:outline-none"
          >
            {s.options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      );
    case 'daterange':
      return (
        <div key={s.key}>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
            {s.label}
          </label>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={s.from}
              max={s.to || undefined}
              onChange={(e) => s.onFromChange(e.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none"
            />
            <span className="flex-shrink-0 text-xs text-gray-400">to</span>
            <input
              type="date"
              value={s.to}
              min={s.from || undefined}
              onChange={(e) => s.onToChange(e.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none"
            />
          </div>
        </div>
      );
    case 'multiselect':
      return (
        <MultiSelectField
          key={s.key}
          label={s.label}
          options={s.options}
          selected={s.selected}
          onToggle={s.onToggle}
          onClear={s.onClear}
          searchable={s.searchable}
          allLabel={s.allLabel}
        />
      );
  }
};

export default function DashboardFilterModal({
  open,
  onClose,
  title,
  sections,
  activeCount,
  onClearAll,
}: Props) {
  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-sm sm:items-center"
      onClick={onClose}
    >
      <div
        className="my-8 w-full max-w-2xl rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3.5">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-gray-900">{title}</h2>
            {activeCount > 0 && (
              <span className="rounded-full bg-primary-100 px-2 py-0.5 text-xs font-semibold text-primary-700">
                {activeCount} active
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {activeCount > 0 && (
              <button
                type="button"
                onClick={onClearAll}
                className="rounded px-2 py-1 text-xs font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              >
                Clear all
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Body — responsive grid of filter controls */}
        <div className="grid max-h-[65vh] grid-cols-1 gap-4 overflow-y-auto px-5 py-4 sm:grid-cols-2">
          {sections.map((s) => renderSection(s))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-700"
          >
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
